import { useState, useEffect, FC, Fragment } from "react";
import { ReactComponent as ExitIcon } from "@images/exit_icon.svg";
import IconButton from "../common/IconButton";
import { useData } from "@providers/DataContext";
import { NLNotificationGroupAsArrivedType, NLUserGroup } from "@models/Models";
import EmptyStateView from "@components/common/EmptyStateView";
import collageGlasses from "@images/collage_search_no_results.png";
import { useHotkeys } from "react-hotkeys-hook";
import { Dialog, Transition } from "@headlessui/react";
import Alert, { AlertState } from "./Alert";
import GroupListRow from "./GroupListRow";
import { useAuth } from "@providers/AuthContext";
import { NLNotificationPreferenceType } from "@models/Enums";
import { setGroupAsArrivedNotification } from "@utils/managers/NotificationsManager";

interface NotificationGroupSelectionModalProps {
	isShow: boolean;
	setShow: (isShow: boolean) => void;
}

const NotificationGroupSelectionModal: FC<NotificationGroupSelectionModalProps> = ({ isShow, setShow }) => {
	const { userGroups } = useData();
	const [selectedGroups, setSelectedGroups] = useState<NLUserGroup[]>([]);
	const [initialSelectedGroups, setInitialSelectedGroups] = useState<NLUserGroup[]>([]);
	const [hasMadeChanges, setHasMadeChanges] = useState<boolean>(false);
	const { authUser, setAuthUser } = useAuth();

	const [alertState, setAlertState] = useState<AlertState>({
		isShow: null,
		title: null,
		message: null,
		actionButton: null,
		dismissButton: null,
		actionButtonAction: undefined,
	});

	useHotkeys("Escape", () => onClose());

	useEffect(() => {
		if (isShow) {
			const alreadyAddedGroups = getAddedGroups();
			setInitialSelectedGroups(alreadyAddedGroups);
			setSelectedGroups(alreadyAddedGroups);
			setHasMadeChanges(false);
		}
	}, [isShow]);

	useEffect(() => {
		const groupChangeMade = changesMade();
		setHasMadeChanges(groupChangeMade);
	}, [selectedGroups]);

	const getAddedGroups = (): NLUserGroup[] => {
		if (!userGroups) return [];
		const alreadySelectedGroupIds = authUser!.notifications?.group_as_arrived?.group_ids ?? [];
		const alreadyAddedGroups = userGroups.filter((x) => alreadySelectedGroupIds.includes(x.id));
		return alreadyAddedGroups;
	};

	const groupSelected = (userGroup: NLUserGroup) => {
		var tempSelectedGroups = [...selectedGroups];
		const selectedIds = tempSelectedGroups.map((x) => x.id);
		if (selectedIds.includes(userGroup.id)) {
			tempSelectedGroups = tempSelectedGroups.filter((x) => x.id != userGroup.id);
		} else {
			tempSelectedGroups.push(userGroup);
		}
		setSelectedGroups(tempSelectedGroups);
	};

	function changesMade(): boolean {
		const initialGroupIds = initialSelectedGroups.map((x) => x.id);
		const selectedGroupIds = selectedGroups.map((x) => x.id);
		const copyArr1 = initialGroupIds.slice();
		const copyArr2 = selectedGroupIds.slice();
		copyArr1.sort();
		copyArr2.sort();
		return JSON.stringify(copyArr1) !== JSON.stringify(copyArr2);
	}

	const onClose = () => {
		setShow(false);
	};

	const saveOnClick = () => {
		if (!selectedGroups) return;
		const newNotifications = new NLNotificationGroupAsArrivedType({ id: "0", type: NLNotificationPreferenceType.groupAsArrived, group_ids: selectedGroups.map((x) => x.id) });
		setAuthUser({ ...authUser!, notifications: { group_as_arrived: newNotifications } });
		setGroupAsArrivedNotification(authUser!, newNotifications);
        onClose();
	};

	return (
		<>
			<Alert alertState={alertState} setAlertState={setAlertState} />
			<Transition as={Fragment} show={isShow}>
				<Dialog static onClose={() => null} className="relative z-40">
					<Transition.Child as="div" enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
						<div className="fixed inset-0 bg-black bg-opacity-70" aria-hidden="true" />
					</Transition.Child>
					<div className="fixed inset-0 flex w-screen items-center justify-center p-4">
						<Transition.Child className="w-full h-full" enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
							<Dialog.Panel className="m-auto w-full h-full max-w-lg transform overflow-hidden rounded-2xl bg-secondary border border-primary-200 text-left align-middle shadow-xl transition-all">
								<div className="absolute w-full bg-surface top-0 left-0 flex flex-col p-4 border-b border-primary-200 z-20 gap-3">
									<div className="flex flex-row justify-between items-center">
										<div className="text-primary font-medium font-primary text-xl">Select groups for notifications</div>
										<IconButton Icon={ExitIcon} className="-mr-2.5" onClick={() => onClose()} />
									</div>
								</div>
								<div className="flex flex-col gap-4 text-left h-full p-4 py-[105px] bg-secondary overflow-auto">
									<div className="text-primary-500 font-regular font-primary text-base">You’ll only receive push notifications from the senders in the groups you select below.</div>
									<div>
										{userGroups ? (
											userGroups.map((userGroup) => {
												return <GroupListRow group={userGroup} isSelected={selectedGroups.map((x) => x.id).includes(userGroup.id)} onSelectedItem={(group) => groupSelected(group)} />;
											})
										) : (
											<EmptyStateView title="No groups found" description="Create a new group to continue" alertImage={collageGlasses} centerInParent={true} />
										)}
									</div>
								</div>
								<div className="absolute bottom-0 w-full bg-surface backdrop-blur-md border-t border-primary-100 px-4 py-4 pt-3 z-20">
									<div className={`flex flex-row justify-end`}>
										<button disabled={selectedGroups.length === 0} className={`${selectedGroups.length !== 0 ? "opacity-100 hover:bg-success-green/80" : "opacity-50"} text-white font-medium font-primary text-base focus:outline-none rounded-xl p-2 px-3 py-2 items-center bg-success-green transition ease-in-out whitespace-nowrap`} onClick={() => saveOnClick()}>
											Done
										</button>
									</div>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</Dialog>
			</Transition>
		</>
	);
};

export default NotificationGroupSelectionModal;
